import { useContext, useState } from 'react'
import { useFirestoreOrderBy, useFirestoreGeneralOrderBy } from '../../firebase/useFirestore'
import researchSharingQuestionnaire from '../../helpers/analysisHelpers/researchSharingQuestionnaire'
import ResearchData from '../Research/ResearchData'
import isQuestionField from '../../helpers/questionnaire/isQuestionField'
import DeepAnalysisSectionHeader from './DeepAnalysisSectionHeader'
import DeepAnalysisPeriodContext from '../../StateManagment/DeepAnalysisPeriodContext'
import ComparisonFrame from '../dataVisualizations/ComparisonFrame'
import periodRangeToDates from '../../helpers/liveReportHelpers/periodRangeToDates'

/**
 * "Onderzoeken samenvoegen" section on a research deep-analysis page.
 *
 * The page's anchor research is the base; the user ticks one or more other
 * researches that use the same questionnaire, and their answers are charted
 * as one combined set per question (reuses ResearchData with an ID array).
 * Display-only — ports the old CombineResearch builder without its
 * save-to-Analysis behavior.
 *
 * Follows the page period (DeepAnalysisPeriodContext); when two periods are
 * compared the combined charts render once per period, side by side.
 * AI-CONTEXT: See docs/features/deep-analysis-period.md
 *
 * @param {object} props
 * @param {object} props.anchor - The anchor Research doc
 */
const ResearchCombineSection = ({ anchor }) => {
    const [selectedExtras, setSelectedExtras] = useState(/** @type {string[]} */ ([]))
    const period = useContext(DeepAnalysisPeriodContext)
    const compare = Boolean(period?.compareActive)

    const allResearchRaw = useFirestoreOrderBy('Research', 'Position', 'asc')
    const allResearch = (allResearchRaw || []).filter((r) => !r.Deleted)
    const combinableResearch = researchSharingQuestionnaire(allResearch, anchor)
    const combinedResearchIds = [anchor.ID, ...selectedExtras]
    const combinedResearchTitles = [anchor, ...combinableResearch.filter((r) => selectedExtras.includes(r.ID))]
        .map((r) => r.Title)
        .join(', ')

    const questionnaireFields = useFirestoreGeneralOrderBy(
        'QuestionnaireFields',
        'QuestionnaireID',
        anchor.QuestionnaireID || '',
        'Position',
        'asc'
    )

    const toggleExtra = (id, checked) => {
        setSelectedExtras((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)))
    }

    /** @param {{start: string|null, end: string|null}|null} range - Period to chart (null = all data) */
    const renderFields = (range) => {
        const { startDate, endDate } = periodRangeToDates(range)
        return (
            <div className='combine-research-data-container'>
                {questionnaireFields.filter(isQuestionField).map((field) => (
                    <ResearchData
                        key={field.ID}
                        fieldType={field.Type}
                        fieldId={field.ID}
                        researchId={combinedResearchIds}
                        researchTitle={combinedResearchTitles}
                        docid={field.docid}
                        graphType={field.GraphType}
                        liveReportHidden={field.LiveReportHidden}
                        paragrapgMomentVisibilityStyle={field.ParagraphMomentVisibilityStyle}
                        startDate={startDate}
                        endDate={endDate}
                    />
                ))}
            </div>
        )
    }

    return (
        <div className='deep-analysis-section'>
            <DeepAnalysisSectionHeader
                kicker='Samenvoegen'
                title='Onderzoeken samenvoegen'
                description={`Voeg "${anchor.Title}" samen met andere onderzoeken die dezelfde vragenlijst gebruiken en bekijk de antwoorden als één geheel.`}
            />
            {combinableResearch.length === 0 ? (
                <p className='deep-analysis-empty'>
                    Er zijn geen andere onderzoeken met dezelfde vragenlijst om mee samen te voegen.
                </p>
            ) : (
                <div className='combine-research-extras'>
                    {combinableResearch.map((r) => (
                        <label key={r.ID} className='deep-analysis-checkbox-label'>
                            <input
                                type='checkbox'
                                value={r.ID}
                                checked={selectedExtras.includes(r.ID)}
                                onChange={(e) => toggleExtra(r.ID, e.target.checked)}
                            />
                            {' '}{r.Title}
                        </label>
                    ))}
                </div>
            )}
            {selectedExtras.length > 0 && Array.isArray(questionnaireFields) && (compare
                ? <ComparisonFrame rangeA={period.rangeA} rangeB={period.rangeB} renderViz={renderFields} />
                : renderFields(period?.rangeA || null))}
        </div>
    )
}

export default ResearchCombineSection
